import { Alert, Badge, Grid, useMantineTheme } from '@mantine/core'
import { selectedPropertyHasPermission } from 'App/Map/PanelDetails/_utils/selectedPropertyHasPermission'
import { useTimezoneForSelectedPropertyOrRegion } from 'App/Map/PanelDetails/_utils/useTimezoneForSelectedPropertyOrRegion'
import { transformCoordsToLatLng } from 'App/ServiceCenter/Map/_utils/transformCoordsToLatLng'
import {
  checkIsTrapConsumableUpdatable,
  openNodeConsumableModal,
} from 'App/ServiceCenter/NodeConsumableModal'
import { openNodeInstallationModal } from 'App/ServiceCenter/NodeInstallation/NodeInstallation'
import { networkStore } from 'App/ServiceCenter/store/networkStore'
import { serviceCenterStore } from 'App/ServiceCenter/store/serviceCenterStore'
import { TRAP_NODE_TYPES } from 'App/ServiceCenter/utils/constants/nodeType'
import { Authorization } from 'components/Authorization/Authorization'
import { Button } from 'components/Button/Button'
import { IconErrorCircle } from 'components/icons/IconErrorCircle'
import { IconRightArrowCircle } from 'components/icons/IconRightArrowCircle'
import { IconWarning } from 'components/icons/IconWarning'
import { MapCard } from 'components/MapCard/MapCard'
import type { Point } from 'geojson'
import { translate } from 'i18n/i18n'
import moment from 'moment-timezone'
import React, { useMemo } from 'react'
import { colors } from 'settings/colors'
import { layersNavigationMenuStore } from 'stores/layersNavigationMenuStore/layersNavigationMenuStore'
import { getIntercomPrePopulatedMessage } from 'utils/getIntercomPrePopulatedMessage'
import { useIsWideScreen } from 'utils/useIsWideScreen'
import type { GeoJSONPoint } from 'wellknown'
import { parse } from 'wellknown'
import { openActionMenu } from '../../ActionMenu/ActionMenu'
import { getEquipmentTypeLabel } from '../../Map/_utils/equipmentTypes'
import {
  getActiveNodeStatus,
  getNodeStatusLabel,
  NODE_STATUS_COLORS,
} from '../../Map/_utils/getActiveNodeStatus'
import { getNodeEquipmentType } from '../../Map/_utils/getNodeEquipmentType'
import { openNodeMaintenanceModal } from '../../NodeMaintenanceModal/NodeMaintenanceModal'
import type { TActiveGateway, TActiveNode, TDnNodeLog, TLnNodeLog, TSnNodeLog } from '../../types'
import { TNodeStatus } from '../../types'
import { apiRequestQueueStore } from '../../utils/api/queue/apiRequestQueueStore'
import { getPlannedDevices } from '../../utils/flatNodeDevices'
import { getIdentifier } from '../../utils/getIdentifier'
import { getNodeActions } from '../../utils/getNodeActions'
import { getNodeNetworkType } from '../../utils/getNodeNetworkType'
import { getNodeTypeLabel } from '../../utils/getNodeTypeLabel'
import { getServiceIssueTitle } from '../../utils/getServiceIssueTitle'
import { DeviceList } from '../DeviceList/DeviceList'
import { DirectionButton } from '../DirectionButton/DirectionButton'
import { FailedRequestError } from '../FailedRequestError/FailedRequestError'
import { DnNodeLog } from './log/DnNodeLog'
import { LnNodeLog } from './log/LnNodeLog'
import { SnNodeLog } from './log/SnNodeLog'
import { TrapLog } from './log/TrapLog'
import { TrapConfigDetail } from './TrapConfigDetail/TrapConfigDetail'
import { TrapLastService } from './TrapLastService/TrapLastService'

type ActiveNodeDetailProps = {
  node: TActiveNode | TActiveGateway
  onClose: () => void
}

export const ActiveNodeDetail: React.FC<ActiveNodeDetailProps> = ({ node, onClose }) => {
  const theme = useMantineTheme()
  const latLng = useMemo(() => transformCoordsToLatLng(JSON.parse(node.location) as Point), [node.location])
  const hasPlannedDevices = useMemo(() => !!getPlannedDevices(node.devices).length, [node])
  const timezone = useTimezoneForSelectedPropertyOrRegion()
  const allFailedRequests = apiRequestQueueStore.useSelector(apiRequestQueueStore.selectors.getFailedRequests)
  const lastLog = serviceCenterStore.useSelector(serviceCenterStore.selectors.getSelectedEquipmentLastLog)
  const isWideScreen = useIsWideScreen()
  const menuCollapsed = layersNavigationMenuStore.useSelector((s) => s.menuCollapsed)
  const prePopulatedMessage = getIntercomPrePopulatedMessage({ node: node }) ?? ''
  const connected = networkStore.useSelector((s) => s.connectionStatus.connected)
  const userCanInstall = selectedPropertyHasPermission({ permission: 'EDIT_SSC_INSTALL_PLANNED_NODE' })
  const userCanViewLogs = selectedPropertyHasPermission({ permission: 'VIEW_SSC_NODE_LAST_LOG' })

  const serviceIssues = serviceCenterStore.useSelector(
    serviceCenterStore.selectors.getSelectedEquipmentServiceIssues,
  )

  const nodeFailedRequests = useMemo(
    () =>
      allFailedRequests.filter((req) => {
        if ('nodeIdentifier' in req.meta) {
          return req.meta.nodeIdentifier === getIdentifier(node)
        } else {
          return false
        }
      }),
    [allFailedRequests, node],
  )

  const unsyncedNodeIdentifiers = apiRequestQueueStore.useSelector(
    apiRequestQueueStore.selectors.getUnsyncedNodeIdentifiers,
  )

  const isUnsynced = useMemo(
    () => unsyncedNodeIdentifiers.includes(getIdentifier(node)),
    [unsyncedNodeIdentifiers, node],
  )

  const handleStartInstallation = () => {
    if (!(node as TActiveNode)?.nodeIdentifier) {
      throw new Error(`node is not a TActiveNode: ${JSON.stringify(node)}`)
    }

    const location = parse(node.location) as GeoJSONPoint

    openNodeInstallationModal({
      payload: {
        location: location,
        nodeIdentifier: (node as TActiveNode).nodeIdentifier,
        nodeType: (node as TActiveNode).nodeType,
        propertyId: (node as TActiveNode).propertyId,
        name: node.name || undefined,
        managementType: (node as TActiveNode).managementType,
        maintenanceOwnerId: (node as TActiveNode).maintenanceOwnerId,
        devices: (node as TActiveNode).devices,
      },
      localState: {
        isLocationConfirmed: true,
      },
    })
  }

  const lastLogText = lastLog
    ? translate.dates.format(moment.tz(lastLog.stamp, 'UTC').tz(timezone), 'MMM D, YYYY h:mm a (z)')
    : translate.phrases.placeholder('No log received')

  let nodeStatus = userCanViewLogs ? getActiveNodeStatus(lastLog) : TNodeStatus.ACTIVE

  if (serviceIssues.length) nodeStatus = TNodeStatus.ERROR

  const nodeStatusText = getNodeStatusLabel(nodeStatus)
  const nodeStatusColor = NODE_STATUS_COLORS[nodeStatus]
  const networkType = getNodeNetworkType(node)
  const nodeType = (node as TActiveNode).nodeType || (node as TActiveGateway).gatewayType
  const nodeTypeLabel = getNodeTypeLabel(nodeType)
  const equipmentType = !!node && getNodeEquipmentType(nodeType)

  let title = node.name!

  if (!node.name) {
    title = translate.phrases.placeholder('{{equipmentType}} - {{nodeId}}', {
      equipmentType: equipmentType
        ? getEquipmentTypeLabel(equipmentType)
        : translate.phrases.placeholder('Node'),
      nodeId: (node as TActiveNode).nodeIdentifier || (node as TActiveGateway).gatewayIdentifier,
    })
  }

  const handleOpenActionMenu = () => {
    const actions = getNodeActions(node, connected)

    openActionMenu(actions)
  }

  const handleServiceIssueClick = () => {
    if (serviceIssues.length === 1) {
      openNodeMaintenanceModal(node as TActiveNode, serviceIssues[0].serviceIssueId)
    } else {
      openNodeMaintenanceModal(node as TActiveNode)
    }
  }

  return (
    <MapCard
      title={title}
      onClose={onClose}
      latLng={latLng}
      cardCSS={menuCollapsed && !isWideScreen ? { zIndex: 10000 } : undefined}
      footer={
        <div css={{ display: 'flex', flexDirection: 'column', flex: '1 1 auto' }}>
          {['trp', 'dn_trp'].includes(nodeType) && (
            <Button
              variant="tertiary"
              size="lg"
              style={{ marginBottom: '8px' }}
              disabled={!checkIsTrapConsumableUpdatable(node as TActiveNode)}
              onClick={() => openNodeConsumableModal()}
            >
              {translate.phrases.placeholder('Manage Trap Consumables')}
            </Button>
          )}

          <div
            css={{
              display: 'flex',
              justifyContent: 'space-between',
              marginBottom: 8,
            }}
          >
            <Button variant="tertiary" size="lg" css={{ flex: '1 1 50%' }} onClick={handleOpenActionMenu}>
              {translate.phrases.placeholder('More Options')}
            </Button>

            {hasPlannedDevices && (
              <Button
                variant="primary"
                disabled={!userCanInstall}
                css={{ marginLeft: 10, flex: '1 1 50%' }}
                onClick={() => handleStartInstallation()}
              >
                {translate.phrases.placeholder('Install')}
              </Button>
            )}
          </div>

          <Button
            style={{
              height: 40,
              width: '100%',
              marginBottom: 10,
              borderColor: 'red',
              backgroundColor: 'white',
              color: 'black',
            }}
            variant="tertiary"
            size="lg"
            // @ts-expect-error
            onClick={() => window.Intercom('showNewMessage', prePopulatedMessage)}
          >
            {translate.phrases.placeholder('Report Issue')}
          </Button>

          <DirectionButton latLng={latLng} />
        </div>
      }
    >
      <div>
        {!!serviceIssues.length && (
          <Alert
            icon={
              <span css={{ color: colors.red }}>
                <IconErrorCircle />
              </span>
            }
            variant="light"
            styles={{
              root: {
                backgroundColor: theme.colors.gray[0],
                boxShadow: '0px 2px 8px 0px rgba(0, 0, 0, 0.15)',
                cursor: 'pointer',
                marginBottom: 12,
              },
              wrapper: { alignItems: 'center' },
              icon: { fontSize: '20px' },
              message: {
                fontSize: '16px',
              },
            }}
          >
            <Button
              variant="link"
              fullWidth={true}
              rightIcon={
                <div css={{ fontSize: '26px', color: colors.primary, height: 26, lineHeight: '26px' }}>
                  <IconRightArrowCircle />
                </div>
              }
              styles={{
                root: {
                  padding: 0,
                },
                inner: {
                  justifyContent: 'space-between',
                  flex: '1 1 auto',
                },
                label: {
                  flex: '1 1 auto',
                },
              }}
              onClick={handleServiceIssueClick}
            >
              <div>
                {serviceIssues.length === 1
                  ? getServiceIssueTitle(node as TActiveNode, serviceIssues[0])
                  : translate.phrases.placeholder('{{equipmentType}} - Multiple issues ({{count}})', {
                      equipmentType: nodeTypeLabel,
                      count: serviceIssues.length.toString(),
                    })}
              </div>
            </Button>
          </Alert>
        )}

        {isUnsynced && (
          <Alert
            icon={
              <span css={{ color: theme.colors.yellow[0] }}>
                <IconWarning />
              </span>
            }
            variant="light"
            styles={{
              root: {
                backgroundColor: 'rgb(255, 249, 219)',
                boxShadow: '0px 2px 8px 0px rgba(0, 0, 0, 0.15)',
              },
              wrapper: { alignItems: 'center' },
              message: { fontSize: 16 },
            }}
          >
            {translate.phrases.placeholder('This node has unsynced changes')}
          </Alert>
        )}

        {nodeFailedRequests?.map((request) => (
          <FailedRequestError key={request.id} request={request} />
        ))}

        <div css={{ padding: '5px 20px 0' }}>
          <Authorization
            requires={{
              permission: 'VIEW_SSC_NODE_LAST_LOG',
              entity: (node as TActiveNode).propertyId,
            }}
          >
            <div
              css={{
                margin: '0 0 16px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <span css={{ color: theme.colors.grey[3], fontSize: 12 }}>{lastLogText}</span>
              <Badge
                css={{
                  color: theme.colors.midnight,
                  backgroundColor: nodeStatusColor,
                  fontSize: '12px',
                  fontWeight: 500,
                  textTransform: 'none',
                }}
              >
                {nodeStatusText}
              </Badge>
            </div>
          </Authorization>

          <Grid css={{ fontSize: '14px', marginBottom: '8px' }}>
            <Grid.Col span={4}>{translate.phrases.placeholder('Equipment ID')}</Grid.Col>
            <Grid.Col span={8}>{getIdentifier(node)}</Grid.Col>

            {TRAP_NODE_TYPES.includes((node as TActiveNode).nodeType) && <TrapConfigDetail />}

            {TRAP_NODE_TYPES.includes((node as TActiveNode).nodeType) && (
              <TrapLastService lastServiceDates={(node as TActiveNode).trap?.lastService} />
            )}

            <Authorization
              requires={{
                permission: 'VIEW_SSC_NODE_LAST_LOG',
                entity: (node as TActiveNode).propertyId,
              }}
            >
              <>
                {TRAP_NODE_TYPES.includes((node as TActiveNode).nodeType) && !!lastLog && (
                  <TrapLog log={lastLog} />
                )}

                {!!lastLog && networkType === 'SN' && (
                  <SnNodeLog
                    log={lastLog as TSnNodeLog}
                    nodeType={(node as TActiveNode).nodeType || (node as TActiveGateway).gatewayType}
                    timezone={timezone}
                  />
                )}

                {!!lastLog && networkType === 'DN' && (
                  <DnNodeLog
                    log={lastLog as TDnNodeLog}
                    nodeType={(node as TActiveNode).nodeType}
                    timezone={timezone}
                  />
                )}

                {!!lastLog && networkType === 'LN' && (
                  <LnNodeLog
                    log={lastLog as TLnNodeLog}
                    nodeType={(node as TActiveNode).nodeType}
                    timezone={timezone}
                  />
                )}
              </>
            </Authorization>
          </Grid>

          {!!node.devices && (
            <>
              <h4 css={{ marginBottom: 10, fontWeight: 500 }}>{translate.phrases.placeholder('Devices')}</h4>
              <DeviceList nodeDevices={node.devices} />
            </>
          )}
        </div>
      </div>
    </MapCard>
  )
}
